import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { drbfmService } from '@services/drbfm.service';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { DRBFMSession, SessionResultsResponse } from '../../../types';

/**
 * 3D viewer navigation. cad_model_id is not on the session row — it lives on
 * each failure_mode's cad_anchor — so fetch the results, pull the first
 * anchored cad_model_id, and pass both to the viewer. Without cad_model in the
 * URL, the viewer's marker-filter (`selectedModel.id === anchor.cadModelId`)
 * returns an empty set and the demo looks broken (TG #673). Fall back to
 * session-only if no anchors are present yet.
 *
 * A ref tracks the latest requested session id — if the user clicks 3D on a
 * different row before the previous fetch resolves, the stale promise is
 * ignored instead of racing the navigate call. A second ref is set on unmount
 * so a pending fetch skips its navigate: clicking 3D and then leaving the page
 * should not pull the user back into the CAD viewer seconds later.
 */
export const useView3DNavigation = () => {
  const navigate = useNavigate();
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const latestRequestRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
      latestRequestRef.current = null;
    },
    [],
  );

  const view3D = useCallback(
    async (session: DRBFMSession) => {
      latestRequestRef.current = session.id;
      setLoadingSessionId(session.id);
      try {
        const results = await drbfmService.getSessionResults(session.id);
        if (unmountedRef.current || latestRequestRef.current !== session.id) return;
        const anchoredCadModelId = results.failureModes?.find((fm) => fm.cadAnchor)?.cadAnchor
          ?.cadModelId;
        const params = new URLSearchParams();
        params.set('drbfm_session', session.id);
        if (anchoredCadModelId) {
          params.set('cad_model', anchoredCadModelId);
        }
        navigate(`/cad-viewer?${params.toString()}`);
      } catch {
        if (unmountedRef.current || latestRequestRef.current !== session.id) return;
        // Fall back: still let the user land on the viewer; they can pick a CAD
        // model manually. The viewer surfaces its own error banner.
        navigate(`/cad-viewer?drbfm_session=${session.id}`);
      } finally {
        if (!unmountedRef.current && latestRequestRef.current === session.id) {
          setLoadingSessionId(null);
        }
      }
    },
    [navigate],
  );

  return { loadingSessionId, view3D };
};

export const useSessionExport = () => {
  const { t } = useTranslation();
  const [exportingSessionId, setExportingSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exportSession = useCallback(
    async (session: Pick<DRBFMSession, 'id' | 'itemName'>) => {
      setExportingSessionId(session.id);
      setError(null);

      try {
        const filename = `drbfm_${session.itemName}_${session.id.slice(0, 8)}.xlsx`;
        await drbfmService.downloadExportedExcel(session.id, filename);
      } catch (err) {
        setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.history.exportError' }));
      } finally {
        setExportingSessionId(null);
      }
    },
    [t],
  );

  return { exportingSessionId, error, clearError: () => setError(null), exportSession };
};

export const useConvertToVerification = () => {
  const { t } = useTranslation();
  const [session, setSession] = useState<DRBFMSession | null>(null);
  const [results, setResults] = useState<SessionResultsResponse | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    async (target: DRBFMSession) => {
      setSession(target);
      setError(null);
      setIsLoadingResults(true);
      try {
        setResults(await drbfmService.getSessionResults(target.id));
        setIsOpen(true);
      } catch {
        setError(t('drbfm.history.resultsFetchFailed'));
        setSession(null);
      } finally {
        setIsLoadingResults(false);
      }
    },
    [t],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setSession(null);
    setResults(null);
  }, []);

  return {
    session,
    results,
    isOpen,
    isLoadingResults,
    error,
    clearError: () => setError(null),
    start,
    close,
  };
};

export const useSessionDeletion = (deleteSession: (sessionId: string) => Promise<unknown>) => {
  const [target, setTarget] = useState<DRBFMSession | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const request = useCallback((session: DRBFMSession) => {
    setTarget(session);
    setIsOpen(true);
  }, []);

  const cancel = useCallback(() => {
    setIsOpen(false);
    setTarget(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!target) return;

    setIsDeleting(true);
    try {
      await deleteSession(target.id);
      setIsOpen(false);
      setTarget(null);
    } catch {
      // Surfaced by the sessions hook's own error state; the dialog stays open.
    } finally {
      setIsDeleting(false);
    }
  }, [deleteSession, target]);

  return { target, isOpen, isDeleting, request, cancel, confirm };
};
